import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router";

import { ActionTypes } from "../../../../constants";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import HTMLRenderer from "../../../Common/Html/HTMLRenderer";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";
import MessageAvatar from "./MessageAvatar";
import RenderDate from "../../../Common/Date/RenderDate";
import {
    getMessageDetails,
    deleteMessage,
} from "../../../../actions/rmsActions";
import { getError, getMessage, getRmsAppCode } from "../../../../reducers";

function Message({
    message,
    error,
    getMessageDetails,
    deleteMessage,
    appCode,
}) {
    const history = useHistory();
    const { id } = useParams();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    useEffect(() => {
        getMessageDetails(id, appCode);
    }, [id, getMessageDetails, appCode]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    const fromName = message?.from?.name?.length
        ? message.from.name
        : "Unknown";
    const initial = fromName.substring(0, 1);
    const subject =
        message?.subject?.length > 0
            ? ` - ${message.subject}`
            : " - No subject";
    const body = message?.body?.length > 0 ? message.body : "";

    const handleDelete = () => {
        deleteMessage(appCode, message.id);
        history.replace("/messages");
    };

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={<MessageAvatar initial={initial} />}
                            title={
                                <Typography
                                    component="h2"
                                    className="card-header-title"
                                >
                                    {fromName}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    {!!message?.createdAt && (
                                        <RenderDate date={message.createdAt} />
                                    )}
                                    {subject}
                                </Typography>
                            }
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    <Button
                                        variant="contained"
                                        color="neutral"
                                        disableElevation
                                        aria-label="delete message"
                                        onClick={() =>
                                            setOpenDeleteDialog(true)
                                        }
                                        startIcon={<DeleteOutlinedIcon />}
                                    >
                                        Delete
                                    </Button>
                                </Stack>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <HTMLRenderer html={body} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <ConfirmDialog
                open={openDeleteDialog}
                title="Delete message"
                content={`The following message will be deleted: ${message.subject}`}
                confirmButtonLabel="Delete"
                onConfirm={handleDelete}
                onCancel={() => setOpenDeleteDialog(false)}
                onClose={() => setOpenDeleteDialog(false)}
            />
        </Container>
    );
}

const mapStateToProps = (state, { match }) => ({
    message: getMessage(state, match.params.id) || {},
    error: getError(state, ActionTypes.GET_MESSAGE_SUCCESS),
    appCode: getRmsAppCode(state),
});

export default connect(mapStateToProps, { getMessageDetails, deleteMessage })(
    Message
);
